import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '@/components/AppIcon';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../../components/ui/Dialog';
import { createRefereeProfile, getCompanyUsers, getRefereesAllPages } from '@/api/TournamentManagement/refereeApi';
import type { CompanyUser } from '@/api/TournamentManagement/refereeApi';
import { RefereeCertification } from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';

interface CreateRefereeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingRefereeUserIds: string[];
}

const CreateRefereeModal: React.FC<CreateRefereeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  existingRefereeUserIds,
}) => {
  const { t } = useTranslation('tournament');
  const { code: currencyCode } = useCurrency();

  const [employees, setEmployees] = useState<CompanyUser[]>([]);
  const [takenUserIds, setTakenUserIds] = useState<string[]>(existingRefereeUserIds);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [certification, setCertification] = useState<string>('');
  const [pointToCurrencyRate, setPointToCurrencyRate] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch company users on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchUsers() {
      setLoadingEmployees(true);
      try {
        // Both, together: the caller only ever knew the referees on the CURRENT page, so with more
        // referees than one page the picker offered people who already had a profile and every pick
        // failed server-side with "A referee profile already exists for this user".
        const [result, refs] = await Promise.all([getCompanyUsers(), getRefereesAllPages()]);
        if (!cancelled && refs.errors.length === 0 && refs.payload) {
          setTakenUserIds(refs.payload.items.map((r) => r.userId));
        }
        if (!cancelled && result.errors.length === 0 && result.payload) {
          setEmployees(result.payload);
        }
      } catch {
        // silently fail — user will see empty list
      } finally {
        if (!cancelled) setLoadingEmployees(false);
      }
    }
    fetchUsers();
    return () => { cancelled = true; };
  }, []);

  // Filter available employees (exclude those already having referee profiles)
  const availableEmployees = useMemo(() => {
    const taken = new Set(takenUserIds);
    const filtered = employees.filter((emp) => !taken.has(emp.id));
    if (!employeeSearch.trim()) return filtered;
    const lowerSearch = employeeSearch.toLowerCase();
    return filtered.filter((emp) => {
      const name = (emp.name ?? '').toLowerCase();
      const email = (emp.email ?? '').toLowerCase();
      return name.includes(lowerSearch) || email.includes(lowerSearch);
    });
  }, [employees, takenUserIds, employeeSearch]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!selectedUserId) newErrors.userId = t('Please select an employee');
    if (certification === '') newErrors.certification = t('Certification is required');
    if (pointToCurrencyRate.trim() !== '' && (Number.isNaN(Number(pointToCurrencyRate)) || Number(pointToCurrencyRate) < 0))
      newErrors.pointToCurrencyRate = t('Rate must be a non-negative number');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const result = await createRefereeProfile({
        userId: selectedUserId,
        certification: Number(certification) as RefereeCertification,
        // Empty = no override (server stores null and the company-wide rule rate applies).
        pointToCurrencyRate: pointToCurrencyRate.trim() === '' ? null : Number(pointToCurrencyRate),
      });

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to create official profile'));
        return;
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setApiError(msg);
      console.error('Error creating referee profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedUserId('');
    setSelectedUserName('');
    setCertification('');
    setPointToCurrencyRate('');
    setEmployeeSearch('');
    setErrors({});
    setApiError('');
    onClose();
  };

  const handleSelectEmployee = (userId: string) => {
    setSelectedUserId(userId);
    const user = employees.find((e) => e.id === userId);
    setSelectedUserName(user?.name ?? '');
    if (errors.userId) {
      setErrors((prev) => ({ ...prev, userId: '' }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent size="lg" data-testid="create-referee-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Icon name="UserPlus" size={22} className="me-2 text-amber-600" />
            {t('Create Official Profile')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-5">
            {/* Employee Picker */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {t('Select Employee')} *
              </label>

              {/* Search */}
              <Input
                type="text"
                value={employeeSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEmployeeSearch(e.target.value)
                }
                placeholder={t('Search employees...')}
              />

              {/* Employee list */}
              <div className="border border-border rounded-md max-h-40 overflow-y-auto">
                {loadingEmployees ? (
                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                    <Icon name="Loader2" size={16} className="animate-spin me-2" />
                    {t('Loading data...')}
                  </div>
                ) : availableEmployees.length === 0 ? (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    {t('No employees available')}
                  </div>
                ) : (
                  availableEmployees.map((emp) => (
                    <button
                      key={emp.id}
                      type="button"
                      onClick={() => handleSelectEmployee(emp.id)}
                      className={`w-full text-start px-3 py-2 text-sm hover:bg-muted transition-colors flex items-center gap-2 ${
                        selectedUserId === emp.id
                          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                          : 'text-foreground'
                      }`}
                      data-testid={`employee-option-${emp.id}`}
                    >
                      <Icon
                        name={selectedUserId === emp.id ? 'CheckCircle2' : 'User'}
                        size={14}
                        className={selectedUserId === emp.id ? 'text-amber-600' : 'text-muted-foreground'}
                      />
                      <span>{emp.name}</span>
                      {emp.email && (
                        <span className="text-xs text-muted-foreground ms-auto">{emp.email}</span>
                      )}
                    </button>
                  ))
                )}
              </div>

              {selectedUserName && (
                <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-1">
                  <Icon name="CheckCircle2" size={14} />
                  {selectedUserName}
                </p>
              )}
              {errors.userId && (
                <p role="alert" className="text-xs text-destructive">{errors.userId}</p>
              )}
            </div>

            {/* Certification */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('Certification')} *
              </label>
              <Select
                value={certification}
                onChange={(value: string) => {
                  setCertification(value);
                  if (errors.certification) {
                    setErrors((prev) => ({ ...prev, certification: '' }));
                  }
                }}
                options={[
                  { label: t('National Level A'), value: String(RefereeCertification.NationalLevelA) },
                  { label: t('AFC Elite'), value: String(RefereeCertification.AFCElite) },
                  { label: t('FIFA International'), value: String(RefereeCertification.FIFAInternational) },
                ]}
                placeholder={t('Select certification')}
                className={errors.certification ? 'border-red-500' : ''}
                aria-invalid={!!errors.certification}
                aria-required="true"
                data-testid="create-referee-certification"
              />
              {errors.certification && (
                <p role="alert" className="text-xs text-destructive mt-1">{errors.certification}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">{t('National Level A: Local tournaments | AFC Elite: Regional | FIFA International: Global', { nsSeparator: false })}</p>
            </div>

            {/* Point to Currency Rate */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('Point to Currency Rate')} {currencyCode && `(${currencyCode})`} <span className="text-muted-foreground font-normal">({t('Optional')})</span>
              </label>
              <Input
                type="number"
                value={pointToCurrencyRate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPointToCurrencyRate(e.target.value);
                  if (errors.pointToCurrencyRate) setErrors((prev) => ({ ...prev, pointToCurrencyRate: '' }));
                }}
                placeholder={t('Leave empty to use company default')}
                min="0"
                step="0.01"
                aria-invalid={!!errors.pointToCurrencyRate}
                data-testid="create-referee-currency-rate"
              />
              {errors.pointToCurrencyRate && (
                <p role="alert" className="text-xs text-destructive mt-1">{errors.pointToCurrencyRate}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">{t('Overrides the company-wide rate from Work Point Settings for this official only. Leave empty to use the company-wide rate.')}</p>
            </div>

            {/* API Error */}
            {apiError && (
              <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center" data-testid="create-referee-api-error">
                <Icon name="AlertCircle" size={16} className="text-red-600 dark:text-red-400 me-2 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-300">{apiError}</p>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              data-testid="create-referee-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              iconName="Plus"
              data-testid="create-referee-submit-btn"
            >
              {isSubmitting ? t('Creating...') : t('Create Official Profile')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRefereeModal;
